import { DeskCardDetail, DeskTimelineItem } from "types/Desk";
import { visibleAttachments } from "components/desk-email-html/desk-email-cids";
import { meetingOneLiner } from "utils/deskMeeting";

/**
 * Flattens an open card into one block of plain text: everything a person (or
 * an AI session) needs to pick the task up — who is waiting, what was already
 * said, and where the card lives — without access to the ERP itself.
 *
 * Kept pure so it can be tested; the panel does the clipboard write.
 */

/** Roughly a long conversation. Past this, a paste stops being pasteable. */
const DEFAULT_MAX_CHARS = 24000;
/** One quoted-history-heavy email must not eat the whole budget. */
const MAX_BODY_CHARS = 4000;

/** The app stamps every date in Vilnius (formatPrettyDate), so a paste read
 *  next to the card must not shift under a traveller's browser clock. */
const TIME_ZONE = "Europe/Vilnius";

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return "—";
  // "sv" renders as 2026-09-04 11:20:31 — unambiguous in any locale.
  return new Date(ms).toLocaleString("sv", { timeZone: TIME_ZONE });
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function fmtAddress(email: string | null, name: string | null): string {
  if (!email) return name || "—";
  return name ? `${name} <${email}>` : email;
}

function cleanBody(text: string | null): string {
  if (!text) return "";
  const normalized = text
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (normalized.length <= MAX_BODY_CHARS) return normalized;
  return `${normalized.slice(0, MAX_BODY_CHARS)}\n…[message truncated]`;
}

function messageBlock(m: DeskTimelineItem, index: number): string {
  const when = fmtDate(m.sentAt || m.createdAt);

  if (m.type === "event") {
    return `[${index}] EVENT ${when} — ${m.eventType || "unknown"}`;
  }

  const lines: string[] = [];
  if (m.type === "note") {
    lines.push(`[${index}] INTERNAL NOTE ${when}`);
  } else {
    const direction = m.type === "inbound" ? "INBOUND" : "OUTBOUND";
    // A failed send is the whole reason a card can look answered and not be.
    const status = m.type === "outbound" && m.sendStatus && m.sendStatus !== "sent" ? ` (${m.sendStatus})` : "";
    lines.push(`[${index}] ${direction}${status} ${when} — from: ${fmtAddress(m.fromEmail, m.fromName)}`);
    const to = (m.toEmails || []).map((a) => a.email).join(", ");
    const cc = (m.ccEmails || []).map((a) => a.email).join(", ");
    if (to) lines.push(`    to: ${to}`);
    if (cc) lines.push(`    cc: ${cc}`);
    if (m.sendError) lines.push(`    send error: ${m.sendError}`);
  }
  const files = visibleAttachments(m).map((a) => a.filename || "file");
  if (files.length) lines.push(`    attachments: ${files.join(", ")}`);
  // An invite's body is a join link; its time lives only in the .ics part.
  if (m.calendarEvent) {
    const summary = m.calendarEvent.summary ? `${m.calendarEvent.summary} — ` : "";
    const cancelled = m.calendarEvent.method === "CANCEL" || m.calendarEvent.status === "CANCELLED";
    lines.push(`    meeting: ${summary}${meetingOneLiner(m.calendarEvent)}${cancelled ? " — CANCELLED" : ""}`);
  }

  const body = cleanBody(m.bodyText);
  lines.push("", body || "(no text content)");
  return lines.join("\n");
}

export interface CardContextOptions {
  companyId: string;
  /** Defaults to the current window origin; injectable for tests. */
  origin?: string;
  maxChars?: number;
}

export function buildCardContext(detail: DeskCardDetail, opts: CardContextOptions): string {
  const { card, timeline, participants } = detail;
  const origin = opts.origin !== undefined ? opts.origin : typeof window !== "undefined" ? window.location.origin : "";
  const maxChars = opts.maxChars || DEFAULT_MAX_CHARS;

  const header: string[] = [
    `Desk card #${card.number} — ${card.title}`,
    `Link: ${origin}/app/${opts.companyId}/desk/inbox?card=${card.number}`,
    `Status: ${card.status} · Priority: ${card.priority} · Created: ${fmtDate(card.createdAt)} · Last message: ${fmtDate(
      card.lastMessageAt
    )} (times: ${TIME_ZONE})`,
  ];
  if (card.emailSubject) header.push(`Subject: ${card.emailSubject}`);
  header.push(`Client: ${card.client ? card.client.name : "—"} · Project: ${card.project ? card.project.name : "—"}`);
  if (card.waitingSince) header.push(`Waiting since: ${fmtDate(card.waitingSince)}`);
  if (participants.length) {
    header.push(
      `Participants: ${participants.map((p) => `${fmtAddress(p.email, p.name)} (${p.role})`).join(", ")}`
    );
  }
  const attachments = timeline
    .flatMap((m) => visibleAttachments(m))
    .map((a) => `${a.filename || "file"} (${fmtSize(a.sizeBytes)})`);
  if (attachments.length) header.push(`Attachments: ${attachments.join(", ")}`);

  const blocks = timeline.map((m, i) => messageBlock(m, i + 1));

  // Keep the newest messages: on a long thread the recent turns are the task,
  // and the opening mail is usually restated in the quoted history anyway.
  const headerText = header.join("\n");
  let budget = maxChars - headerText.length - 200;
  const kept: string[] = [];
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const cost = blocks[i].length + 2;
    if (kept.length && cost > budget) break;
    budget -= cost;
    kept.unshift(blocks[i]);
  }
  const omitted = blocks.length - kept.length;

  const parts = [
    headerText,
    "",
    `--- Thread (${timeline.length} entries, oldest first) ---`,
  ];
  if (omitted > 0) parts.push("", `…${omitted} earlier ${omitted === 1 ? "entry" : "entries"} omitted…`);
  parts.push("", kept.join("\n\n"));
  return parts.join("\n").trim();
}
